/**
 * Theme preview (screenshots/80, right column): six sample charts drawn by ECharts with the theme itself — colors come
 * from `echarts.registerTheme()` and switch with ECharts 6 `chart.setTheme()`, never from per-series colors — so what you
 * see is exactly what a widget with this theme draws. "See it as" swaps in a simulated copy of the theme
 * (Machado 2009 / grayscale, paletteChecks.simulate). All sample data, as the header says.
 */
import { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts/core";
import { simulate, type Vision } from "./paletteChecks";
import { toEChartsTheme, type ChartTheme } from "./themes";
import "./ChartWidget";   // registers the ECharts components (echarts.use) once

export type SeeAs = "normal" | Vision | "gray";

/** The theme as seen with a color-vision deficiency (or in grayscale), registered under its own id. */
export function simulatedTheme(t: ChartTheme, as: SeeAs): ChartTheme {
  if (as === "normal") return t;
  const sim = (c: string) => simulate(c, as);
  return { ...t, id: `${t.id}--${as}`, palette: t.palette.map(sim), sequential: t.sequential.map(sim) as ChartTheme["sequential"],
    diverging: t.diverging.map(sim) as ChartTheme["diverging"] };
}

const ax = { axisLine: { show: false }, axisTick: { show: false } };
const grid = { left: 8, right: 8, top: 8, bottom: 8, containLabel: true };
export const SAMPLES: { title: string; option: () => object }[] = [
  { title: "Bar · one color", option: () => ({ grid, xAxis: { type: "value", show: false }, yAxis: { type: "category", inverse: true, data: ["Member A", "Member B", "Member C", "Member D", "Member E"], ...ax },
    series: [{ type: "bar", data: [30, 22, 19, 15, 10], barWidth: 8 }] }) },
  { title: "Line · two series", option: () => ({ grid, xAxis: { type: "category", show: false, data: [1, 2, 3, 4, 5, 6, 7, 8] }, yAxis: { type: "value", axisLabel: { show: false }, ...ax },
    series: [{ type: "line", data: [5, 7, 6, 9, 11, 10, 13, 16] }, { type: "line", data: [3, 4, 4, 6, 5, 7, 6, 8] }] }) },
  { title: "Donut · three parts", option: () => ({ series: [{ type: "pie", radius: ["48%", "78%"], label: { show: false }, data: [{ value: 50, name: "A" }, { value: 30, name: "B" }, { value: 20, name: "C" }] }] }) },
  { title: "Stacked 100%", option: () => ({ grid, xAxis: { type: "value", show: false, max: 100 }, yAxis: { type: "category", show: false, data: ["x", "y", "z"] },
    series: [[48, 40, 55], [30, 32, 25], [22, 28, 20]].map((data) => ({ type: "bar", stack: "s", data, barWidth: 14 })) }) },
  { title: "Heatmap · sequential", option: () => ({ grid: { ...grid, containLabel: false }, xAxis: { type: "category", show: false, data: [0, 1, 2, 3, 4, 5] }, yAxis: { type: "category", show: false, data: [0, 1, 2, 3] },
    visualMap: { show: false, min: 0, max: 3, calculable: false }, series: [{ type: "heatmap", itemStyle: { borderColor: "#fff", borderWidth: 2 },
      data: Array.from({ length: 24 }, (_, i) => [i % 6, Math.floor(i / 6), (i * 7 + Math.floor(i / 6)) % 4]) }] }) },
  { title: "Columns · four groups", option: () => ({ grid, xAxis: { type: "category", show: false, data: ["x", "y"] }, yAxis: { type: "value", show: false },
    series: [[9, 8], [6, 9], [5, 5], [3, 7]].map((data) => ({ type: "bar", data, barWidth: 9 })) }) },
];

function Sample({ title, option, themeId, themeKey }: { title: string; option: () => object; themeId: string; themeKey: string }) {
  const el = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | null>(null);
  useEffect(() => {
    const c = echarts.init(el.current!, themeId, { renderer: "canvas" });
    c.setOption({ ...option(), animation: false });
    chart.current = c;
    const ro = new ResizeObserver(() => c.resize());
    ro.observe(el.current!);
    return () => { ro.disconnect(); c.dispose(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Live switch: re-register (same id, new colors) and setTheme; no re-init.
  useEffect(() => { chart.current?.setTheme(themeId); }, [themeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return <figure className="theme-sample"><figcaption>{title}</figcaption><div ref={el} className="theme-sample__chart" role="img" aria-label={`${title}, sample chart`} /></figure>;
}

export function ThemePreviewGrid({ theme, as }: { theme: ChartTheme; as: SeeAs }) {
  const shown = useMemo(() => simulatedTheme(theme, as), [theme, as]);
  const key = `${shown.id}:${shown.palette.join()}:${shown.sequential.join()}:${shown.barRadius}:${shown.tooltip}:${shown.lineWidth}`;
  echarts.registerTheme(shown.id, toEChartsTheme(shown) as Parameters<typeof echarts.registerTheme>[1]);
  return <div className="theme-preview__grid">{SAMPLES.map((s) => <Sample key={s.title} {...s} themeId={shown.id} themeKey={key} />)}</div>;
}
